import { useNavigate } from 'react-router-dom';
import { FolderKanban } from 'lucide-react';
import { Badge } from '@proerp/ui';
import { formatMoney } from '@/lib/money';
import { usePermissions } from '@/lib/useCurrentUser';
import { useProjects } from '@/features/projects/projectsApi';
import { ProjectAmpelBadge } from '@/features/projects/statusBadge';
import { ampelBreakdown, criticalProjects } from '../dashboardMetrics';
import { TaskCard, TaskRow } from './TaskCard';

/**
 * Projektgesundheit: Ampel-Verteilung + die kritischsten aktiven Projekte. Der
 * Auftragswert je Zeile ist eine Finanzangabe und erscheint nur mit
 * dashboard.viewfinancials (serverseitig ist er ohnehin gestrippt, ADR 0062).
 */
export function ProjectHealthWidget() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const projects = useProjects({ pageSize: 200 });

  const projectList = projects.data?.data ?? [];
  const ampel = ampelBreakdown(projectList);
  const critical = criticalProjects(projectList);
  const canViewFinancials = can('dashboard.viewfinancials');

  return (
    <TaskCard
      title="Projektgesundheit"
      icon={FolderKanban}
      isLoading={projects.isLoading}
      isEmpty={critical.length === 0}
      emptyText="Alle aktiven Projekte im grünen Bereich."
      header={
        !projects.isLoading && (
          <span className="flex items-center gap-2 text-xs">
            <Badge tone="success">Grün {ampel.GRUEN}</Badge>
            <Badge tone="warning">Gelb {ampel.GELB}</Badge>
            <Badge tone="danger">Rot {ampel.ROT}</Badge>
          </span>
        )
      }
    >
      {critical.slice(0, 6).map((project) => (
        <TaskRow
          key={project.id}
          onClick={() => navigate(`/projekte/${project.id}`)}
          left={
            <>
              <span className="font-mono text-xs text-muted-foreground">{project.code}</span>
              <span className="ml-2 text-foreground">{project.name}</span>
              {project.customerName && (
                <span className="ml-2 text-xs text-muted-foreground">{project.customerName}</span>
              )}
            </>
          }
          right={
            <span className="flex items-center gap-2">
              {canViewFinancials && project.orderValue && (
                <span className="tabular-nums text-foreground">{formatMoney(project.orderValue)}</span>
              )}
              <ProjectAmpelBadge ampel={project.ampel} />
            </span>
          }
        />
      ))}
    </TaskCard>
  );
}
